import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { MDSModal } from "@/mds";
import type {
  AppId,
  WelcomeWizardGroup,
  WelcomeWizardProps,
  WelcomeStepContext,
  StepMap,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.types";
import { WizardContainer } from "@/components/onboarding/components/welcome-wizard/welcome-wizard.styles";
import { AnimatePresence, motion } from "motion/react";
import { Steps } from "@/components/onboarding/components/welcome-wizard/welcome-wizard.config";
import styled from "@emotion/styled";
import { AppleNotesStepsMap } from "@/components/onboarding/components/welcome-wizard/steps/import/apple-notes-steps";
import { ObsidianStepsMap } from "@/components/onboarding/components/welcome-wizard/steps/import/obsidian-steps";
import { OtherAppsStepsMap } from "@/components/onboarding/components/welcome-wizard/steps/import/other-apps-steps";

const IMPORT_WIZARD_STEPS: StepMap = {
  ...AppleNotesStepsMap,
  ...ObsidianStepsMap,
  ...OtherAppsStepsMap,
};

export const ImportWizard = observer<WelcomeWizardProps>(
  ({ isOpen, onClose, initialStep = "apple-notes-1" }) => {
    const [stepContext, setStepContext] = useState<WelcomeStepContext>({
      wizardType: "import",
      selectedAppId: "apple-notes" as AppId | null,
      customAppName: "",
    });

    const [contentHeight, setContentHeight] = useState<number>(360);
    const wizardContainerRef = useRef<HTMLDivElement>(null);
    const [wizardStep, _setWizardStep] = useState<string>(initialStep);
    const expStepRef = useRef<HTMLDivElement>(null);

    const currentStepConfig = useMemo(() => {
      return Steps[wizardStep];
    }, [wizardStep]);

    const setHeightForStep = useCallback(
      (step: string) => {
        const doWork = () => {
          if (currentStepConfig?.fullHeight) {
            setContentHeight(5000);
            return;
          }

          const stepEl = document.querySelector(
            `[data-wizard-step="${step}"]`
          ) as HTMLElement | null;
          if (stepEl) {
            const vPadding = 56; // 28 top and bottom
            const newHeight = stepEl.scrollHeight + vPadding;
            setContentHeight(newHeight);
          } else {
            requestAnimationFrame(doWork);
          }
        };
        requestAnimationFrame(doWork);
      },
      [currentStepConfig]
    );

    useEffect(() => {
      if (isOpen) setHeightForStep(wizardStep);
    }, [isOpen, setHeightForStep, wizardStep]);

    const gotoWizardStep = useCallback((step: string) => {
      _setWizardStep(step);
    }, []);

    const handleClose = (group: WelcomeWizardGroup, ...rest: string[]) => {
      onClose(group, ...rest);
    };

    // Generic context update handler
    const handleUpdateContext = useCallback((updates: Partial<WelcomeStepContext>) => {
      setStepContext(prev => ({ ...prev, ...updates }));
    }, []);

    const handleTabSelect = useCallback(
      (appId: AppId) => {
        let nextWizardStep = "other-apps-1";

        switch (appId) {
          case "apple-notes":
            nextWizardStep = "apple-notes-1";
            handleUpdateContext({ selectedAppId: appId });
            setHeightForStep("apple-notes-1");
            break;
          case "obsidian":
            nextWizardStep = "obsidian-1";
            handleUpdateContext({ selectedAppId: appId });
            setHeightForStep("obsidian-1");
            break;
          case "other":
            nextWizardStep = "other-apps-1";
            handleUpdateContext({ selectedAppId: appId });
            setHeightForStep("other-apps-1");
            break;
          default:
            nextWizardStep = "other-apps-1";
            handleUpdateContext({ selectedAppId: "other" });
            setHeightForStep("other-apps-1");
            break;
        }

        gotoWizardStep(nextWizardStep);
      },
      [gotoWizardStep, handleUpdateContext, setHeightForStep]
    );

    return (
      <MDSModal isOpen={isOpen} handleClose={() => handleClose("import-cta")}>
        <ModalContent>
          <WizardContainer
            data-component="wizard-container"
            layout="position"
            layoutRoot
            layoutScroll
            animate={{ height: contentHeight }}
            ref={wizardContainerRef}
          >
            {Object.entries(IMPORT_WIZARD_STEPS).map(([stepName, stepConfig]) => {
              return (
                <AnimatePresence key={stepName}>
                  {wizardStep === stepName && (
                    <StepCrossFade
                      key={stepName}
                      layout="position"
                      ref={wizardStep === stepName ? expStepRef : undefined}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      top={"28px"}
                      data-wizard-step={stepName}
                    >
                      {stepConfig.render({
                        // New generic props
                        context: stepContext,
                        onUpdateContext: handleUpdateContext,
                        onNavToStep: (step: string) => {
                          gotoWizardStep(step);
                          setHeightForStep(step);
                        },
                        onClose: (...rest: string[]) => handleClose(stepConfig.group, ...rest),

                        // Props for import steps
                        selectedAppId: stepContext.selectedAppId || null,
                        customAppName: stepContext.customAppName || "",
                        onSetSelectedApp: (appId: AppId) =>
                          handleUpdateContext({ selectedAppId: appId }),
                        onSetCustomAppName: (name: string) =>
                          handleUpdateContext({ customAppName: name }),
                        handleTabSelect,
                      })}
                    </StepCrossFade>
                  )}
                </AnimatePresence>
              );
            })}
          </WizardContainer>
        </ModalContent>
      </MDSModal>
    );
  }
);

const StepCrossFade = styled(motion.div)<{ top: string }>`
  position: fixed;
  display: flex;
  top: ${({ top }) => top};
  left: ${({ theme }) => theme.spacing["6"]};
  right: ${({ theme }) => theme.spacing["6"]};
  max-height: calc(100% - 48px);
`;

const ModalContent = styled.div`
  overflow: hidden;
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  transform: translate(-50%, -50%);
`;
